/* eslint-disable @atlassian/perf-linting/no-quadratic-complexity */
import { type GraphQLTaggedNode } from 'react-relay';
import {
	type IEnvironment,
	type RecordProxy,
	type RecordSourceProxy,
	type ReaderFragment,
	type ReaderLinkedField,
	type ReaderScalarField,
	type ReaderSelection,
} from 'relay-runtime';

import { formatRelayStorageArgs, getRelayFieldStorageKey } from './relayStorageKeys.ts';

export type WithTypename<T> =
	T extends Array<infer U>
		? Array<WithTypename<U>>
		: T extends Record<string, any>
			? { [K in keyof T]: WithTypename<T[K]> } & { __typename?: string }
			: T;

function isObjectRecord(value: unknown): value is Record<string, unknown> {
	return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

type RelayRecordReference = { __ref: string };

function isRelayRecordReference(value: unknown): value is RelayRecordReference {
	return isObjectRecord(value) && typeof value.__ref === 'string';
}

type RelayRecordReferences = { __refs: ReadonlyArray<string | null | undefined> };

function isRelayRecordReferences(value: unknown): value is RelayRecordReferences {
	return isObjectRecord(value) && Array.isArray(value.__refs);
}

function getFieldResponseKey(field: ReaderLinkedField | ReaderScalarField): string {
	return field.alias ?? field.name;
}

type RelayScalarValue = string | number | boolean | null | undefined;

function isRelayScalarValue(value: unknown): value is RelayScalarValue | RelayScalarValue[] {
	if (Array.isArray(value)) {
		return value.every(isRelayScalarValue);
	}

	return (
		value === null ||
		value === undefined ||
		typeof value === 'string' ||
		typeof value === 'number' ||
		typeof value === 'boolean'
	);
}

type RecordProxyWithUnsafeValue = RecordProxy & {
	setValue__UNSAFE?: (value: unknown, name: string) => RecordProxy;
};

function setRelayScalarValue(record: RecordProxy, fieldKey: string, scalarValue: unknown): boolean {
	if (scalarValue === undefined) {
		return false;
	}

	if (isRelayScalarValue(scalarValue)) {
		record.setValue(scalarValue, fieldKey);
		return true;
	}

	if (isObjectRecord(scalarValue)) {
		const unsafeRecord = record as RecordProxyWithUnsafeValue;
		if (typeof unsafeRecord.setValue__UNSAFE === 'function') {
			// JSON/custom scalar fields are opaque scalar payloads even when their runtime value is
			// an object. RecordProxy#setValue rejects objects, but Relay's response normalizer stores
			// these values directly through RelayModernRecord.
			unsafeRecord.setValue__UNSAFE(scalarValue, fieldKey);
			return true;
		}
	}

	return false;
}

function canSetRelayScalarValue(record: RecordProxy, scalarValue: unknown): boolean {
	if (scalarValue === undefined || isRelayScalarValue(scalarValue)) {
		return true;
	}

	if (isObjectRecord(scalarValue)) {
		const unsafeRecord = record as RecordProxyWithUnsafeValue;
		return typeof unsafeRecord.setValue__UNSAFE === 'function';
	}

	return false;
}

const addTypenameToSelections = (
	data: Record<string, unknown>,
	selections: readonly ReaderSelection[],
	// Recursive calls pass true only after they already own a copy, so nested
	// typename additions can reuse that object instead of cloning it again.
	hasCopiedData = false,
): { value: Record<string, unknown> & { __typename?: string }; changed: boolean } => {
	if (!data || typeof data !== 'object') {
		return { value: data, changed: false };
	}

	let result = data as Record<string, unknown> & { __typename?: string };
	let changed = hasCopiedData;

	for (const selection of selections) {
		if (selection.kind === 'LinkedField') {
			const responseKey = getFieldResponseKey(selection);
			const fieldData = result[responseKey];

			if (fieldData && typeof fieldData === 'object') {
				if (Array.isArray(fieldData)) {
					let nextItems: unknown[] | null = null;

					fieldData.forEach((item, index) => {
						if (isObjectRecord(item)) {
							let nextItem = item as Record<string, unknown> & { __typename?: string };
							let itemChanged = false;

							if (selection.concreteType && nextItem.__typename == null) {
								nextItem = { ...nextItem };
								nextItem.__typename = selection.concreteType;
								itemChanged = true;
							}
							if (selection.selections) {
								const nestedResult = addTypenameToSelections(
									nextItem,
									selection.selections,
									itemChanged,
								);
								if (nestedResult.changed) {
									nextItem = nestedResult.value;
									itemChanged = true;
								}
							}

							if (itemChanged) {
								nextItems ??= [...fieldData];
								nextItems[index] = nextItem;
							}
						}
					});

					if (nextItems) {
						if (!changed) {
							result = { ...result };
							changed = true;
						}
						result[responseKey] = nextItems;
					}
				} else if (isObjectRecord(fieldData)) {
					let nextFieldData = fieldData as Record<string, unknown> & { __typename?: string };
					let fieldChanged = false;

					if (selection.concreteType && nextFieldData.__typename == null) {
						nextFieldData = { ...nextFieldData };
						nextFieldData.__typename = selection.concreteType;
						fieldChanged = true;
					}
					if (selection.selections) {
						const nestedResult = addTypenameToSelections(
							nextFieldData,
							selection.selections,
							fieldChanged,
						);
						if (nestedResult.changed) {
							nextFieldData = nestedResult.value;
							fieldChanged = true;
						}
					}
					if (fieldChanged) {
						if (!changed) {
							result = { ...result };
							changed = true;
						}
						result[responseKey] = nextFieldData;
					}
				}
			}
		} else if (selection.kind === 'InlineFragment') {
			if (selection.type && result.__typename == null) {
				if (!changed) {
					result = { ...result };
					changed = true;
				}
				result.__typename = selection.type;
			}
			if (selection.selections) {
				const nestedResult = addTypenameToSelections(result, selection.selections, changed);
				if (nestedResult.changed) {
					result = nestedResult.value;
					changed = true;
				}
			}
		} else if (selection.kind === 'Condition') {
			if (selection.selections) {
				const nestedResult = addTypenameToSelections(result, selection.selections, changed);
				if (nestedResult.changed) {
					result = nestedResult.value;
					changed = true;
				}
			}
		}
	}

	return { value: result, changed };
};

export const addTypenameToData = <TData extends Record<string, any>>(
	data: TData,
	fragmentArtifact: ReaderFragment,
): WithTypename<TData> => {
	if (!data || typeof data !== 'object' || !fragmentArtifact) {
		return data as WithTypename<TData>;
	}

	let result = data as Record<string, unknown> & { __typename?: string };
	let changed = false;

	if (fragmentArtifact.type && result.__typename == null) {
		result = { ...result };
		result.__typename = fragmentArtifact.type;
		changed = true;
	}

	if (fragmentArtifact.selections) {
		const selectionResult = addTypenameToSelections(result, fragmentArtifact.selections, changed);
		if (selectionResult.changed) {
			return selectionResult.value as WithTypename<TData>;
		}
	}

	if (!changed) {
		return data as WithTypename<TData>;
	}

	return result as WithTypename<TData>;
};

function getRelayRootFieldId(apolloRootFieldId: string) {
	const match = /^([^()]+)(?:\((.*)\))?$/.exec(apolloRootFieldId);
	if (!match) {
		return `client:root:${apolloRootFieldId}`;
	}

	const [, fieldName, serializedArgs] = match;
	if (!serializedArgs) {
		return `client:root:${fieldName}`;
	}

	try {
		const args = JSON.parse(serializedArgs);
		if (isObjectRecord(args)) {
			return `client:root:${fieldName}${formatRelayStorageArgs(args)}`;
		}
	} catch {
		// If the Apollo id is not JSON-parseable, keep the original suffix so the lookup
		// degrades to a normal cache miss instead of throwing from a cache read path.
	}

	return `client:root:${apolloRootFieldId}`;
}

function apolloFragmentIdToRelayFragmentId(apolloFragmentId: string) {
	if (apolloFragmentId.startsWith('$ROOT_QUERY.')) {
		return getRelayRootFieldId(apolloFragmentId.replace(/^\$ROOT_QUERY\./, ''));
	}

	if (apolloFragmentId.startsWith('$')) {
		const [baseId, ...linkedPath] = apolloFragmentId.slice(1).split('.');
		if (baseId && linkedPath.length > 0) {
			return `client:${baseId}:${linkedPath.join(':')}`;
		}
	}

	return apolloFragmentId;
}

function getFieldKey(
	field: ReaderLinkedField | ReaderScalarField,
	variables: Record<string, any> = {},
): string {
	return getRelayFieldStorageKey(field, variables);
}

type ReadFieldsResult = {
	data: Record<string, unknown>;
	missing: boolean;
};

export function readFragmentShim<TData>(
	environment: IEnvironment,
	fragment: GraphQLTaggedNode,
	fragmentId: string,
	variables?: Record<string, any>,
): TData | null {
	const source = environment.getStore().getSource();
	const relayFragmentId = apolloFragmentIdToRelayFragmentId(fragmentId);
	const rootRecord = source.get(relayFragmentId);

	if (!rootRecord) {
		return null;
	}

	const readFields = (
		recordId: string,
		fields: readonly ReaderSelection[],
	): ReadFieldsResult | null => {
		const record = source.get(recordId);
		if (!record) {
			return null;
		}

		const data: Record<string, unknown> = {};
		let missing = false;
		for (const field of fields) {
			if (field.kind === 'Condition') {
				const conditionValue = variables?.[field.condition];
				const shouldInclude = field.passingValue ? conditionValue : !conditionValue;

				if (shouldInclude) {
					const conditionData = readFields(recordId, field.selections);
					if (conditionData) {
						Object.assign(data, conditionData.data);
						missing = conditionData.missing || missing;
					} else {
						missing = true;
					}
				}
			} else if (field.kind === 'LinkedField') {
				const fieldKey = getFieldKey(field, variables ?? {});
				const responseKey = getFieldResponseKey(field);
				const linkedRecordRef = record[fieldKey];
				const readLinkedRecord = (linkedRecordId: string | null | undefined) => {
					if (linkedRecordId == null) {
						return null;
					}

					const linkedData = readFields(linkedRecordId, field.selections);
					if (!linkedData) {
						missing = true;
						return null;
					}

					missing = linkedData.missing || missing;
					return linkedData.data;
				};

				if (linkedRecordRef === undefined) {
					missing = true;
				} else if (Array.isArray(linkedRecordRef)) {
					data[responseKey] = linkedRecordRef.map((item) => {
						if (item == null) {
							return null;
						}

						if (!isRelayRecordReference(item)) {
							missing = true;
							return null;
						}

						return readLinkedRecord(item.__ref);
					});
				} else if (isRelayRecordReferences(linkedRecordRef)) {
					data[responseKey] = linkedRecordRef.__refs.map(readLinkedRecord);
				} else if (isRelayRecordReference(linkedRecordRef)) {
					data[responseKey] = readLinkedRecord(linkedRecordRef.__ref);
				} else if (linkedRecordRef === null) {
					data[responseKey] = null;
				} else {
					data[responseKey] = null;
					missing = true;
				}
			} else if (field.kind === 'ScalarField') {
				const value = record[getFieldKey(field, variables ?? {})];
				if (value === undefined) {
					missing = true;
				} else {
					data[getFieldResponseKey(field)] = value;
				}
			} else if (field.kind === 'InlineFragment') {
				const fragmentData = readFields(recordId, field.selections);
				if (fragmentData) {
					Object.assign(data, fragmentData.data);
					missing = fragmentData.missing || missing;
				} else {
					missing = true;
				}
			} else {
				continue;
			}
		}
		return { data, missing };
	};

	const readerFragment = fragment as ReaderFragment;
	const readResult = readFields(relayFragmentId, readerFragment?.selections ?? []);
	if (!readResult || readResult.missing) {
		return null;
	}

	return readResult.data as TData;
}

function getRelayDataIdFromWriteData(data: Record<string, unknown>): string | null {
	const id = data.id;
	const typename = data.__typename;

	if (typeof id !== 'string' && typeof id !== 'number') {
		return null;
	}

	const idString = String(id);
	if (typeof typename === 'string') {
		return idString.startsWith(`${typename}:`) ? idString : `${typename}:${idString}`;
	}

	return idString.includes(':') ? idString : null;
}

function getRecordForLinkedData(
	storeProxy: RecordSourceProxy,
	linkedData: Record<string, unknown>,
	existingRecords: ReadonlyArray<RecordProxy | null | undefined>,
	index?: number,
): RecordProxy | null {
	const dataId = getRelayDataIdFromWriteData(linkedData);
	if (dataId) {
		const recordFromStore = storeProxy.get(dataId);
		if (recordFromStore) {
			return recordFromStore;
		}

		return existingRecords.find((record) => record?.getDataID() === dataId) ?? null;
	}

	return index === undefined ? null : (existingRecords[index] ?? null);
}

function getLinkedRecordsForWrite(
	storeProxy: RecordSourceProxy,
	linkedData: readonly unknown[],
	existingRecords: ReadonlyArray<RecordProxy | null | undefined>,
): Array<RecordProxy | null> | null {
	const nextRecords: Array<RecordProxy | null> = [];

	for (let index = 0; index < linkedData.length; index++) {
		const item = linkedData[index];
		if (item == null) {
			nextRecords.push(null);
		} else if (isObjectRecord(item)) {
			const linkedRecord = getRecordForLinkedData(storeProxy, item, existingRecords, index);

			if (!linkedRecord) {
				return null;
			}

			nextRecords.push(linkedRecord);
		} else {
			return null;
		}
	}

	return nextRecords;
}

function areRecordIdListsEqual(
	leftRecords: ReadonlyArray<RecordProxy | null>,
	rightRecords: ReadonlyArray<RecordProxy | null>,
): boolean {
	if (leftRecords.length !== rightRecords.length) {
		return false;
	}

	return leftRecords.every(
		(record, index) => (record?.getDataID() ?? null) === (rightRecords[index]?.getDataID() ?? null),
	);
}

export function writeFragmentShim<TData>(
	environment: IEnvironment,
	fragment: GraphQLTaggedNode,
	fragmentId: string,
	data: TData,
	variables?: Record<string, any>,
): boolean {
	if (!data) {
		return false;
	}

	const relayFragmentId = apolloFragmentIdToRelayFragmentId(fragmentId);
	let didWrite = false;

	environment.commitUpdate((storeProxy: RecordSourceProxy) => {
		const record = storeProxy.get(relayFragmentId);
		if (!record) {
			return;
		}

		const canWriteFields = (
			targetRecord: RecordProxy | null,
			fields: readonly ReaderSelection[],
			writeData: Record<string, unknown>,
		): boolean => {
			if (!writeData) {
				return false;
			}

			for (const field of fields) {
				if (field.kind === 'Condition') {
					const conditionValue = variables?.[field.condition];
					const shouldInclude = field.passingValue ? conditionValue : !conditionValue;

					if (shouldInclude && !canWriteFields(targetRecord, field.selections, writeData)) {
						return false;
					}
					continue;
				}

				if (field.kind === 'LinkedField') {
					const responseKey = getFieldResponseKey(field);
					const hasLinkedData = Object.prototype.hasOwnProperty.call(writeData, responseKey);
					const linkedData = writeData[responseKey];
					const fieldKey = getFieldKey(field, variables ?? {});

					if (Array.isArray(linkedData)) {
						const existingRecords = targetRecord?.getLinkedRecords(fieldKey) ?? [];
						const nextRecords = getLinkedRecordsForWrite(storeProxy, linkedData, existingRecords);

						if (!nextRecords) {
							return false;
						}

						for (let index = 0; index < nextRecords.length; index++) {
							const item = linkedData[index];
							const linkedRecord = nextRecords[index];
							if (
								linkedRecord &&
								isObjectRecord(item) &&
								!canWriteFields(linkedRecord, field.selections, item)
							) {
								return false;
							}
						}
					} else if (isObjectRecord(linkedData)) {
						const linkedRecord =
							targetRecord?.getLinkedRecord(fieldKey) ??
							getRecordForLinkedData(storeProxy, linkedData, []);

						if (linkedRecord) {
							if (!canWriteFields(linkedRecord, field.selections, linkedData)) {
								return false;
							}
						} else if (
							!field.concreteType ||
							getRelayDataIdFromWriteData(linkedData) !== null ||
							!canWriteFields(null, field.selections, linkedData)
						) {
							return false;
						}
					} else if (hasLinkedData && linkedData !== undefined) {
						return false;
					}
					continue;
				}

				if (field.kind === 'ScalarField') {
					const responseKey = getFieldResponseKey(field);
					if (
						Object.prototype.hasOwnProperty.call(writeData, responseKey) &&
						!(targetRecord
							? canSetRelayScalarValue(targetRecord, writeData[responseKey])
							: writeData[responseKey] === undefined || isRelayScalarValue(writeData[responseKey]))
					) {
						return false;
					}
					continue;
				}

				if (
					field.kind === 'InlineFragment' &&
					!canWriteFields(targetRecord, field.selections, writeData)
				) {
					return false;
				}
			}

			return true;
		};

		const writeFields = (
			recordId: string,
			fields: readonly ReaderSelection[],
			writeData: Record<string, unknown>,
		): boolean => {
			const targetRecord = storeProxy.get(recordId);
			if (!targetRecord || !writeData) {
				return false;
			}

			let didWriteFields = false;
			for (const field of fields) {
				if (field.kind === 'Condition') {
					const conditionValue = variables?.[field.condition];
					const shouldInclude = field.passingValue ? conditionValue : !conditionValue;

					if (shouldInclude) {
						didWriteFields = writeFields(recordId, field.selections, writeData) || didWriteFields;
					}
					continue;
				}

				if (field.kind === 'LinkedField') {
					const linkedData = writeData[getFieldResponseKey(field)];
					const fieldKey = getFieldKey(field, variables ?? {});

					if (Array.isArray(linkedData)) {
						const existingRecords = targetRecord.getLinkedRecords(fieldKey) ?? [];
						const nextRecords = getLinkedRecordsForWrite(storeProxy, linkedData, existingRecords);

						if (!nextRecords) {
							return false;
						}

						for (let index = 0; index < nextRecords.length; index++) {
							const item = linkedData[index];
							const linkedRecord = nextRecords[index];
							if (linkedRecord && isObjectRecord(item)) {
								didWriteFields =
									writeFields(linkedRecord.getDataID(), field.selections, item) || didWriteFields;
							}
						}

						if (!areRecordIdListsEqual(existingRecords, nextRecords)) {
							targetRecord.setLinkedRecords(nextRecords, fieldKey);
							didWriteFields = true;
						}
					} else if (isObjectRecord(linkedData)) {
						let linkedRecord =
							targetRecord.getLinkedRecord(fieldKey) ??
							getRecordForLinkedData(storeProxy, linkedData, []);
						if (
							!linkedRecord &&
							field.concreteType &&
							getRelayDataIdFromWriteData(linkedData) === null
						) {
							linkedRecord = targetRecord.getOrCreateLinkedRecord(fieldKey, field.concreteType);
							didWriteFields = true;
						}
						if (linkedRecord) {
							didWriteFields =
								writeFields(linkedRecord.getDataID(), field.selections, linkedData) ||
								didWriteFields;
						}
					}
					continue;
				}

				if (field.kind === 'ScalarField') {
					const scalarValue = writeData[getFieldResponseKey(field)];
					if (setRelayScalarValue(targetRecord, getFieldKey(field, variables ?? {}), scalarValue)) {
						didWriteFields = true;
					}
					continue;
				}

				if (field.kind === 'InlineFragment') {
					didWriteFields = writeFields(recordId, field.selections, writeData) || didWriteFields;
				}
			}

			return didWriteFields;
		};

		const writerFragment = fragment as ReaderFragment;
		if (writerFragment?.selections && isObjectRecord(data)) {
			// Relay commitUpdate does not roll back partial mutations. Validate the selected
			// fragment tree first, then write only when every selected field can be represented.
			if (canWriteFields(record, writerFragment.selections, data)) {
				didWrite = writeFields(relayFragmentId, writerFragment.selections, data);
			}
		}
	});

	return didWrite;
}
